import type { ButtonProps } from "@chakra-ui/react"
import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Link,
  Spacer,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"
import { isAddress } from "@ethersproject/address"
import type { FC, ReactNode } from "react"
import React, { useMemo } from "react"
import truncate from "truncate"

import SmallDelegateUserIdentity from "@/common/components/columns/SmallDelegateUserIdentity"
import StatBox from "@/common/components/StatBox"
import { EXTERNAL_ROUTES, ROUTES } from "@/common/constants/routes"
import { getWeightLabel, stringToBigNumber } from "@/common/helpers/bignumber"
import DelegateButton from "@/delegation/components/DelegateButton"
import DelegateDisabledModal from "@/delegation/components/DelegateDisabledModalProps"
import DelegateWrongNetworkModal from "@/delegation/components/DelegateWrongNetworkModal"
import {
  FeatureFlag,
  useOrganization,
} from "@/organization/providers/OrganizationProvider"
import type { Account, Delegate, Organization } from "@/query/graphql"
import TwitterIcon from "@/ui/components/icons/TwitterIcon"
import { isSolanaAddress } from "@/web3/helpers/address"
import { useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import StakeAccountIntercept from "@/web3/solana/wormhole-staking/StakeAccountIntercept"
import { useStakeAccount } from "@/web3/solana/wormhole-staking/StakeAccountProvider"
import StakingDelegateModal from "@/web3/solana/wormhole-staking/StakingDelegateModal"
import { WORMHOLE_TOKEN_DECIMALS } from "@/web3/solana/wormhole-staking/useMultigovStaking"
import { getWhiteLabelRoute } from "@/whitelabel/utils/breadcrumb"

type AccountCardDetailedProps = {
  delegate: Delegate
  organization: Organization
  detailed: boolean
  onSelectAccount?: (account: Account) => void
  handleDelegateSelected?: (delegate: string) => void
  isStakeMode?: boolean
  isWhiteLabel: boolean
  displayProfileLink?: boolean
  displayDelegateButton?: boolean
  dataQa: string
  karmaScore?: ReactNode // if defined, will display scores from karma
  eligibleBadge?: ReactNode
  buttonProps?: ButtonProps
}
const AccountCardDetailed: FC<AccountCardDetailedProps> = ({
  delegate,
  organization,
  onSelectAccount,
  handleDelegateSelected,
  isStakeMode = false,
  isWhiteLabel,
  displayProfileLink = true,
  displayDelegateButton = true,
  eligibleBadge,
  dataQa,
  karmaScore,
  buttonProps,
}) => {
  const stakeAccountDisclosure = useDisclosure()
  const stakingDelegateDisclosure = useDisclosure()
  const wrongNetworkDisclosure = useDisclosure()
  const delegateDisabledDisclosure = useDisclosure()
  const { stakeAccount } = useStakeAccount()
  const { multichainAccount } = useMultichainAccount()
  const { isFeatureFlagOn } = useOrganization()
  const { address } = multichainAccount

  const displayDelegateSolanaButton = !isFeatureFlagOn(
    FeatureFlag.HideDelegateSolanaButton,
  )

  const { account: account, statement, votesCount, token } = delegate
  const statementSummary = statement?.statementSummary || account.bio
  const bigVoteWeight = stringToBigNumber(votesCount)

  const hasToken = Boolean(token)

  const getVoteWeightFormatted = () => {
    if (!token) {
      return undefined
    }

    const { decimals } = token

    return decimals !== undefined
      ? getWeightLabel(
          bigVoteWeight,
          isSolanaAddress(account.address) ? WORMHOLE_TOKEN_DECIMALS : decimals,
        )
      : undefined // decimals != undefined instead of only decimals as 0 (decimals) is considered falsy
  }

  const tokenSymbol = token?.symbol ?? undefined

  const voteWeightFormatted = getVoteWeightFormatted()

  const twitterUsername = account.twitter

  const handleSelectAccount = () => {
    if (onSelectAccount) {
      onSelectAccount(account as Account)
    }
  }

  const profileLink = useMemo(() => {
    if (!organization) {
      return null
    }

    return isWhiteLabel
      ? getWhiteLabelRoute(
          ROUTES.governance.delegate.profile(
            organization.slug as string,
            delegate.account.address,
          ),
        )
      : ROUTES.governance.delegate.profile(
          organization.slug as string,
          delegate.account.address,
        )
  }, [delegate.account.address, isWhiteLabel, organization])

  const handleSolanaDelegate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (
      isAddress(address) &&
      delegate.account.address &&
      isSolanaAddress(delegate.account.address) &&
      !wrongNetworkDisclosure.isOpen
    ) {
      wrongNetworkDisclosure.onOpen()

      return
    }

    if (
      isSolanaAddress(delegate.account.address) &&
      !displayDelegateSolanaButton
    ) {
      delegateDisabledDisclosure.onOpen()

      return
    }

    if (!stakeAccount) {
      stakeAccountDisclosure.onOpen()

      return
    }

    stakingDelegateDisclosure.onOpen()
  }

  return (
    <Stack
      _hover={{
        borderColor: "gray.300",
      }}
      background="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      className="no-underline"
      direction="column"
      flex={1}
      height="auto"
      pb={2}
      pt={4}
      px={4}
      spacing={4}
      {...(displayProfileLink
        ? {
            as: Link,
            href: profileLink as string,
            onClick: (e: React.MouseEvent) => {
              if ((e.target as HTMLElement).closest("button")) {
                e.preventDefault()
              }
            },
          }
        : {})}
    >
      <Stack overflow="hidden">
        <HStack alignItems="flex-start">
          <SmallDelegateUserIdentity
            account={delegate?.account}
            avatarSize={38}
            displayProfileLink={displayProfileLink}
            isWhiteLabel={isWhiteLabel}
            organization={organization}
          />

          <Spacer />

          <HStack align="center" justify="flex-end" spacing={4} width="50%">
            {Boolean(eligibleBadge) ? <>{eligibleBadge}</> : null}

            <StatBox
              alignItems="center"
              containerHeight={38}
              label={truncate(tokenSymbol ?? "", 6)}
              labelSize="14px"
              size="14px"
              value={<Text align="right">{voteWeightFormatted}</Text>}
              variant="inline"
            />
          </HStack>
        </HStack>
        <Text
          color={!statementSummary ? "gray.400" : "gray.700"}
          fontSize="sm"
          mb={3}
          minHeight="50px"
          noOfLines={2}
        >
          {statementSummary
            ? truncate(statementSummary, 80)
            : "No bio provided"}
        </Text>

        {karmaScore ? karmaScore : null}
      </Stack>

      <HStack alignItems="flex-start">
        <HStack justifyContent="space-between" minWidth="100%">
          {hasToken ? (
            <TrustedByText delegatorsCount={delegate.delegatorsCount} />
          ) : null}
          <Spacer />

          <Center
            border="1px"
            borderColor="gray.200"
            borderRadius="full"
            h="31px"
            maxH="31px"
            minW="31px"
            ml={2}
            w="31px"
          >
            {twitterUsername ? (
              <Tooltip fontSize="lg" label={`@${twitterUsername}`}>
                <Link
                  isExternal
                  href={EXTERNAL_ROUTES.twitter.handle(twitterUsername)}
                >
                  <Icon as={TwitterIcon} bg="white" fill="external.twitter" />
                </Link>
              </Tooltip>
            ) : (
              <Tooltip fontSize="lg" label="No twitter linked">
                <Icon as={TwitterIcon} bg="white" color="gray.200" />
              </Tooltip>
            )}
          </Center>

          {isStakeMode && handleDelegateSelected ? (
            <Button
              fontSize="xs"
              px={3}
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDelegateSelected(delegate.account.address)
              }}
              {...buttonProps}
            >
              Select
            </Button>
          ) : null}

          {Boolean(onSelectAccount) && !Boolean(isStakeMode) ? (
            <Button
              fontSize="xs"
              px={3}
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSelectAccount()
              }}
              {...buttonProps}
            >
              Select
            </Button>
          ) : null}

          {displayDelegateButton &&
          !Boolean(onSelectAccount) &&
          !Boolean(isStakeMode) &&
          organization &&
          !isSolanaAddress(delegate?.account?.address) &&
          organization?.tokenIds?.length > 0 ? (
            <DelegateButton
              account={delegate?.account}
              borderColor="gray.200"
              dataQa={dataQa}
              delegateAddress={delegate?.account?.address}
              fontSize="xs"
              organization={organization}
              px={3}
              size="sm"
              variant="secondary"
              onWrongNetworkOpen={wrongNetworkDisclosure.onOpen}
              {...buttonProps}
            >
              Delegate
            </DelegateButton>
          ) : null}

          {displayDelegateButton &&
          !Boolean(onSelectAccount) &&
          isSolanaAddress(delegate?.account?.address) ? (
            <Button
              borderColor="gray.200"
              fontSize="xs"
              px={3}
              size="sm"
              variant="secondary"
              onClick={handleSolanaDelegate}
              {...buttonProps}
            >
              Delegate
            </Button>
          ) : null}
        </HStack>
      </HStack>
      <StakeAccountIntercept
        isOpen={stakeAccountDisclosure.isOpen}
        onClose={stakeAccountDisclosure.onClose}
      />
      <StakingDelegateModal
        delegateeAccount={delegate?.account}
        isOpen={stakingDelegateDisclosure.isOpen}
        onClose={stakingDelegateDisclosure.onClose}
      />
      <DelegateWrongNetworkModal
        isOpen={wrongNetworkDisclosure.isOpen}
        network={isAddress(address) ? "Solana" : "EVM"}
        onClose={wrongNetworkDisclosure.onClose}
      />
      <DelegateDisabledModal
        isOpen={delegateDisabledDisclosure.isOpen}
        onClose={delegateDisabledDisclosure.onClose}
      />
    </Stack>
  )
}

export default AccountCardDetailed

type TrustedByTextProps = {
  delegatorsCount?: number
}

const TrustedByText: FC<TrustedByTextProps> = ({ delegatorsCount }) => {
  if (delegatorsCount === undefined) return null

  if (delegatorsCount <= 0)
    return (
      <Text
        color="gray.400"
        lineHeight=".9rem"
        ml="4px"
        textStyle="body.regular.sm"
      >
        No delegations to this account
      </Text>
    )

  return (
    <Box>
      <Text
        color="gray.600"
        fontSize="xs"
        fontWeight="regular"
        lineHeight=".9rem"
      >
        Trusted by {delegatorsCount}{" "}
        {delegatorsCount > 1 ? "accounts" : "account"}
      </Text>
    </Box>
  )
}
